<template>
  <div class="home">
    <!-- Top Navigation Bar -->
    <nav class="top-nav">
      <!-- Brand/Logo Section - kept here for the Home view -->
      <div class="nav-brand">
        <div class="brand-logo">
          <span class="brain-icon">🧠</span>
          <span class="brand-text">L2L.fun</span>
        </div>
        <div class="brand-mission">Live to Learn</div>
      </div>
      
      <div class="nav-items">
        <button 
          v-for="(game, index) in games" 
          :key="index"
          :class="{ active: activeGame === index }"
          @click="activeGame = index"
        >
          {{ game.name }}
        </button>
      </div>
    </nav>

    <div class="main-content-wrapper">
      <!-- Left sidebar ads column -->
      <div v-if="showAds" class="ad-sidebar ad-left">
        <!-- Vertical banner ad (160×600) -->
        <div class="ad-container">
          <div v-if="isLocalhost" class="dev-ad-placeholder">
            <span>Responsive Ad<br>Left Sidebar</span>
          </div>
          <div v-else class="ad-size-enforcer" style="width:160px; height:600px;">
            <ins class="adsbygoogle"
                 style="display:block;"
                 :data-ad-client="adPublisherId"
                 :data-ad-slot="adUnits.leftSidebar"
                 data-ad-format="auto"
                 data-full-width-responsive="false"></ins>
          </div>
        </div>
        
        <!-- Square ad (160×160) below the vertical banner -->
        <div class="ad-container ad-square">
          <div v-if="isLocalhost" class="dev-ad-placeholder dev-ad-square">
            <span>Responsive Ad<br>Left Square</span>
          </div>
          <div v-else class="ad-size-enforcer" style="width:160px; height:160px;">
            <ins class="adsbygoogle"
                 style="display:block;"
                 :data-ad-client="adPublisherId"
                 :data-ad-slot="adUnits.leftSidebarSquare"
                 data-ad-format="auto"
                 data-full-width-responsive="false"></ins>
          </div>
        </div>
      </div>
      
      <!-- Main content - Game Area -->
      <div class="main-content">
        <!-- Game #0: Quantum Go -->
        <div v-if="activeGame === 5" class="game-wrapper">
          <div class="header">
            <h1>⚛ Quantum Go</h1>
            <p>Exploring the Wonderful World of Quantum Mechanics - Superposition, Entanglement, and Measurement</p>
          </div>

          <div class="game-container">
            <div class="left-section">
              <GameBoard />
              <div class="bottom-info">
                <ScoreBoard class="stats-section" />
                <Tutorial class="tutorial-section" />
              </div>
            </div>
            <ControlPanel class="right-section" />
          </div>

          <Notification />
          <GameOverModal />
        </div>

        <!-- Game #1: Match 3 -->
        <div v-if="activeGame === 1" class="game-wrapper">
          <Match3Game />
        </div>

        <!-- Game #2: Tetris -->
        <div v-if="activeGame === 2" class="game-wrapper">
          <TetrisGame />
        </div>

        <!-- Game #3: 2048 -->
        <div v-if="activeGame === 3" class="game-wrapper">
          <Game2048 />
        </div>

        <!-- Game #4: Snake -->
        <div v-if="activeGame === 4" class="game-wrapper">
          <SnakeGame />
        </div>

        <!-- Game #5: Equator -->
        <div v-if="activeGame === 0" class="game-wrapper">
          <EquatorGame />
        </div>

        <!-- Placeholders for other games -->
        <div v-if="activeGame > 5" class="game-wrapper placeholder-game">
          <h2>{{ games[activeGame].name }}</h2>
          <p>Coming Soon!</p>
        </div>
      </div>
      
      <!-- Right sidebar ads column -->
      <div v-if="showAds" class="ad-sidebar ad-right">
        <!-- Vertical banner ad (160×600) -->
        <div class="ad-container">
          <div v-if="isLocalhost" class="dev-ad-placeholder">
            <span>Ad Placeholder<br>(160×600)</span>
          </div>
          <div v-else class="ad-size-enforcer" style="width:160px; height:600px;">
            <ins class="adsbygoogle"
                 style="display:block;"
                 :data-ad-client="adPublisherId"
                 :data-ad-slot="adUnits.rightSidebar"
                 data-ad-format="auto"
                 data-full-width-responsive="false"></ins>
          </div>
        </div>
        
        <!-- Square ad (160×160) below the vertical banner -->
        <div class="ad-container ad-square">
          <div v-if="isLocalhost" class="dev-ad-placeholder dev-ad-square">
            <span>Ad Placeholder<br>(160×160)</span>
          </div>
          <div v-else class="ad-size-enforcer" style="width:160px; height:160px;">
            <ins class="adsbygoogle"
                 style="display:block;"
                 :data-ad-client="adPublisherId"
                 :data-ad-slot="adUnits.rightSidebarSquare"
                 data-ad-format="auto"
                 data-full-width-responsive="false"></ins>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Bottom banner ad -->
    <div v-if="showAds" class="ad-bottom">
      <div class="ad-container">
        <div v-if="isLocalhost" class="dev-ad-placeholder dev-ad-banner">
          <span>Multiplex Ad<br>(Bottom Banner)</span>
        </div>
        <div v-else class="ad-size-enforcer" style="width:100%; min-width:300px; min-height:250px;">
          <ins class="adsbygoogle"
               style="display:block;"
               :data-ad-client="adPublisherId"
               :data-ad-slot="adUnits.bottomBanner"
               data-ad-format="autorelaxed"
               data-full-width-responsive="true"></ins>
        </div>
      </div>
    </div>

    <!-- SEO Content Section (Only for Quantum Go for now) -->
    <div v-if="activeGame === 5" class="seo-content-section">
      <div class="seo-scrollview">
        <div class="seo-content">
          <h2>Quantum Go - The Ultimate Strategic Puzzle Game with Quantum Mechanics</h2>
          
          <h3>What is Quantum Go?</h3>
          <p>Quantum Go is an innovative and captivating strategic puzzle game that brilliantly combines the ancient art of Go with the fascinating principles of quantum mechanics. This unique gaming experience introduces players to the wonderful world of quantum physics through superposition, entanglement, and measurement mechanics, creating an entirely new dimension of strategic gameplay that challenges both beginners and expert players alike.</p>
          
          <h3>Why Play Quantum Go?</h3>
          <p>Quantum Go offers an unparalleled gaming experience that stimulates your mind while teaching fundamental quantum physics concepts. Unlike traditional board games, Quantum Go incorporates quantum superposition, allowing stones to exist in multiple states simultaneously until observed or measured. This creates layers of strategic complexity that make every game session exciting and unpredictable. Players develop critical thinking skills, learn about quantum entanglement effects, and experience the thrill of making strategic decisions in a probabilistic environment where outcomes depend on quantum measurements.</p>
          
          <h3>How to Play Quantum Go</h3>
          <p>The gameplay mechanics are intuitive yet deeply strategic. Players place quantum stones on the board that can exist in superposition states, meaning they simultaneously occupy multiple positions until collapsed through measurement. The game features quantum operations like Hadamard gates for creating superposition, CNOT gates for entanglement between stones, and measurement operations that collapse quantum states into classical positions. Each move requires careful consideration of quantum probabilities and their effects on the overall board state. Players must master both traditional Go strategy and quantum mechanical principles to achieve victory.</p>
          
          <h3>Game Features and Mechanics</h3>
          <p>Quantum Go includes comprehensive tutorial systems, real-time score tracking, and interactive quantum operation panels that guide players through complex quantum concepts. The game board visualizes quantum states using sophisticated graphics, showing superposition clouds and entanglement connections between stones. Advanced features include quantum measurement tools, state visualization, and probability calculators that help players understand the quantum aspects of their moves. The scoring system rewards both territorial control and efficient use of quantum operations, creating multiple paths to victory.</p>
          
          <h3>Educational Value and Learning Benefits</h3>
          <p>This game serves as an excellent educational tool for understanding quantum mechanics principles. Students, educators, and quantum physics enthusiasts can explore concepts like wave function collapse, quantum entanglement, and measurement theory through interactive gameplay. The game simplifies complex quantum concepts into accessible game mechanics, making quantum physics education engaging and fun. Players naturally develop intuition about quantum behavior, probability distributions, and the fundamental differences between classical and quantum systems.</p>
          
          <h3>Strategic Depth and Replayability</h3>
          <p>Every game of Quantum Go presents unique challenges due to the probabilistic nature of quantum mechanics. The combination of quantum operations creates virtually unlimited strategic possibilities, ensuring high replayability value. Advanced players can explore sophisticated quantum strategies involving multi-stone entanglement, complex superposition patterns, and timing-dependent measurement sequences. The game rewards both aggressive territorial expansion and subtle quantum manipulation tactics, appealing to diverse playing styles and strategic preferences.</p>
          
          <h3>Perfect for All Skill Levels</h3>
          <p>Whether you're a complete beginner to quantum physics or an experienced researcher, Quantum Go adapts to your skill level. The integrated tutorial system gradually introduces quantum concepts, starting with basic superposition and progressing to advanced entanglement strategies. Beginners can focus on learning fundamental quantum operations while experienced players can develop sophisticated multi-dimensional strategies. The game provides helpful hints, probability calculations, and strategic suggestions to support learning at every level.</p>
          
          <h3>Why Quantum Go is Fun and Addictive</h3>
          <p>The excitement comes from the unpredictable nature of quantum measurements and the satisfaction of executing complex quantum strategies successfully. Players experience genuine surprise and delight when quantum effects produce unexpected outcomes, creating memorable gaming moments. The visual feedback system makes quantum concepts tangible and exciting, with spectacular animations for quantum operations and measurement events. Social competitive elements encourage players to challenge friends and develop increasingly sophisticated strategies over time.</p>
        </div>
      </div>
    </div>

    <!-- SEO Content Section for Mission Snake -->
    <div v-if="activeGame === 4" class="seo-content-section">
      <div class="seo-scrollview">
        <div class="seo-content">
          <h2>Mission Snake - The Ultimate Educational Vocabulary Game</h2>
          
          <h3>What is Mission Snake?</h3>
          <p>Mission Snake is a revolutionary educational game that transforms the classic snake gameplay into an immersive vocabulary learning experience. Players guide their cyber-snake through a neon-lit grid to complete specific missions, such as "Find Fruits" or "Collect Farm Animals." It combines the addictive mechanics of arcade gaming with effective object classification and language learning, making it perfect for students, language learners, and puzzle enthusiasts.</p>
          
          <h3>Educational Benefits</h3>
          <p>This game is designed to enhance vocabulary retention and cognitive classification skills. By actively searching for specific categories of items (like Planets, Tools, or Vegetables) while avoiding distractors, players reinforce their understanding of word associations and categories. The game features high-quality images and native pronunciation audio for hundreds of words, providing a multi-sensory learning environment that improves memory and pronunciation.</p>
          
          <h3>Game Features</h3>
          <p>Mission Snake features a modern, glassmorphism-inspired interface with glowing neon visuals. The game includes dynamic difficulty adjustment, where the snake speed increases as you complete missions, challenging your reflexes and quick thinking. With over 50 unique categories and hundreds of items to collect, the game offers endless replayability. The "Mission" system keeps gameplay fresh by constantly changing objectives, ensuring players never get bored.</p>
          
          <h3>How to Play</h3>
          <p>Use the arrow keys to navigate your snake. Your goal is to eat items that match the current mission displayed at the top of the screen. For example, if the mission is "Find Fruits," you must guide the snake to apples, bananas, and grapes while avoiding distractors like cars or animals. Eating the correct item scores points, plays the pronunciation audio, and grows your snake. Be careful—eating the wrong item costs a life!</p>
          
          <h3>Why Play Mission Snake?</h3>
          <p>Whether you're learning a new language, teaching a child vocabulary, or just looking for a fun brain teaser, Mission Snake delivers. It turns rote memorization into an exciting challenge. The combination of fast-paced arcade action and educational content creates a "flow state" where learning happens naturally and effortlessly. Challenge yourself to beat your high score while mastering new words and categories!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GameBoard from '@/components/GameBoard.vue';
import ControlPanel from '@/components/ControlPanel.vue';
import Notification from '@/components/Notification.vue';
import GameOverModal from '@/components/GameOverModal.vue';
import ScoreBoard from '@/components/ScoreBoard.vue';
import Tutorial from '@/components/Tutorial.vue';

export default {
  name: 'Home',
  components: {
    GameBoard,
    ControlPanel,
    Notification,
    GameOverModal,
    ScoreBoard,
    Tutorial,
    Match3Game: () => import('@/components/Match3Game.vue'),
    TetrisGame: () => import('@/components/TetrisGame.vue'),
    Game2048: () => import('@/components/Game2048.vue'),
    SnakeGame: () => import('@/components/SnakeGame.vue'),
    EquatorGame: () => import('@/components/EquatorGame.vue')
  },
  data() {
    return {
      // Set to true to enable ads in web version
      // Ads will never show in Electron regardless of this setting
      adsEnabled: true,
      
      // Your Google AdSense publisher ID
      adPublisherId: 'ca-pub-2036003728175540', // Publisher ID
      
      // Ad unit IDs for different placements
      adUnits: {
        leftSidebar: '7096368339', // Your left sidebar ad unit ID
        rightSidebar: '3969418217', // Your right sidebar ad unit ID
        leftSidebarSquare: '9067925558', // Your left sidebar square ad unit ID
        rightSidebarSquare: '9030173209', // Your right sidebar square ad unit ID
        bottomBanner: '3773977091', // Your bottom banner ad unit ID
      },
      
      // Whether the current environment is Electron
      isElectron: window.electron !== undefined,
      
      // Whether we're running on localhost (for development)
      isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
      
      adsInitialized: false,
      adRefreshInterval: null,
      
      activeGame: 0,
      games: [
        { name: 'Equator', id: 0 },
        { name: 'Word Match 3', id: 1 },
        { name: 'Math Tetris', id: 2 },
        { name: 'Evolute 2048', id: 3 },
        { name: 'Mission Snake', id: 4 },
        { name: 'Quantum Go', id: 5 }
      ]
    };
  },
  
  computed: {
    showAds() {
      return this.adsEnabled && !this.isElectron;
    }
  },
  
  mounted() {
    // Initialize game
    this.$store.dispatch('game/initBoard');
    this.$store.dispatch('quantum/initQuantumState');
    
    // Delay check game status to ensure board initialization is complete
    setTimeout(() => {
      this.$store.dispatch('game/checkGameEnd');
    }, 1000);
    
    // Only load AdSense script if ads are enabled and we're not in Electron
    if (this.showAds && !this.isLocalhost) {
      // Check if script already exists (it might if we navigated back)
      if (!document.querySelector(`script[src*="adsbygoogle"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${this.adPublisherId}`;
        document.head.appendChild(script);
        
        // Initialize ads after script loads
        script.onload = () => {
          this.initializeAds();
        };
      } else {
        // If script exists, just init ads
        this.initializeAds();
      }
    }
  },
  
  methods: {
    initializeAds() {
      // Skip on localhost
      if (this.isLocalhost) {
        console.log('AdSense disabled on localhost');
        return;
      }
      
      // Initial ad load
      const initAds = () => {
        try {
          // Select and refresh all ad units
          document.querySelectorAll('.adsbygoogle').forEach(adElement => {
            if (!adElement.hasAttribute('data-adsbygoogle-status')) {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
              adElement.setAttribute('data-adsbygoogle-status', 'initialized');
            }
          });
        } catch (e) {
          console.error('Ad initialization error:', e);
        }
      };
      
      // Initial load
      setTimeout(initAds, 1000);
      
      // Auto-refresh every 60 seconds
      this.adRefreshInterval = setInterval(() => {
        initAds();
      }, 60000);
    },
    
    beforeDestroy() {
      // Clear ad refresh interval
      if (this.adRefreshInterval) {
        clearInterval(this.adRefreshInterval);
      }
    }
  }
};
</script>

<style scoped>
/* Copied from App.vue but scoped */
.home {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.main-content-wrapper {
  display: flex;
  width: 100%;
}

.header {
  text-align: center;
  color: white;
  margin-bottom: 30px;
}

.header h1 {
  font-size: 2.5em;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.header p {
  font-size: 1.1em;
  opacity: 0.9;
}

.game-container {
  display: flex;
  gap: 30px;
  justify-content: center;
  align-items: flex-start;
}

.left-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bottom-info {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.stats-section, .tutorial-section {
  width: 300px;
  height: 540px;
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 0;
}

.right-section {
  width: 300px;
}

/* Ad layout styles */
.main-content {
  flex: 1;
  min-width: 0; /* Ensure the content can shrink if needed */
}

/* Ad sidebar */
.ad-sidebar {
  width: 160px;
  min-width: 160px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 0 10px;
  border-radius: 8px;
}

.ad-container {
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

/* Ad size enforcer */
.ad-size-enforcer {
  position: relative;
  overflow: hidden;
}

.ad-size-enforcer .adsbygoogle {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.ad-bottom {
  height: 280px;
  margin-top: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.ad-bottom .ad-container {
  height: 100%;
}

/* Development ad placeholders */
.dev-ad-placeholder {
  background-color: rgba(255, 255, 255, 0.2);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  font-size: 14px;
  font-family: monospace;
}

.ad-sidebar .dev-ad-placeholder {
  width: 160px;
  height: 600px;
}

.dev-ad-square {
  width: 160px;
  height: 160px;
}

.dev-ad-placeholder span {
  padding: 10px;
}

.ad-square {
  margin-top: 20px;
}

.dev-ad-banner {
  width: 100%;
  max-width: 728px;
  height: 280px;
}

/* SEO Content Section */
.seo-content-section {
  margin-top: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
}

.seo-scrollview {
  height: 60px; /* 3 lines tall at ~20px per line */
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 10px;
}

.seo-content {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 14px;
}

.seo-content h2 {
  font-size: 18px;
  margin-bottom: 15px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.seo-content h3 {
  font-size: 16px;
  margin: 20px 0 10px 0;
  color: #e6f3ff;
  font-weight: bold;
}

.seo-content p {
  margin-bottom: 15px;
  text-align: justify;
  opacity: 0.95;
}

/* Custom scrollbar for SEO content */
.seo-scrollview::-webkit-scrollbar {
  width: 6px;
}

.seo-scrollview::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.seo-scrollview::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.seo-scrollview::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

@media (max-width: 768px) {
  .header h1 {
    font-size: 1.8em;
  }

  .game-container {
    flex-direction: column;
    align-items: center;
  }
  
  .bottom-info {
    flex-direction: column;
  }
  
  .ad-sidebar {
    display: none; /* Hide side ads on mobile */
  }
  
  .main-content {
    width: 100%;
  }
  
  .seo-content-section {
    margin-top: 15px;
  }
  
  .seo-content h2 {
    font-size: 16px;
  }
  
  .seo-content h3 {
    font-size: 14px;
  }
  
  .seo-content {
    font-size: 13px;
  }
}

/* Top Navigation Styles */
.top-nav {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 15px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.nav-brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brain-icon {
  font-size: 24px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.brand-text {
  font-size: 1.5em;
  font-weight: 800;
  background: linear-gradient(to right, #fff, #e0eaff);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 10px rgba(0,0,0,0.1);
  letter-spacing: -0.5px;
}

.brand-mission {
  font-size: 0.7em;
  color: rgba(255,255,255,0.8);
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-left: 36px; /* Align with text */
}

.nav-items {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.nav-items button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9em;
}

.nav-items button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.nav-items button.active {
  background: white;
  color: #667eea;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.placeholder-game {
  text-align: center;
  color: white;
  padding: 50px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
}
</style>
